import React, { useCallback, useEffect, useState, useRef } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";

import formatTime from "../../../utils/formatTime";
import convertToSeconds from "../../../utils/convertStringToSeconds";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

export type TTimeInputProps = Omit<TextFieldProps, "onBlur"> & {
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    totalSeconds: number
  ) => void;
};

const TimeInput: React.FC<TTimeInputProps> = ({
  value = "",
  onBlur,
  sx, // Extract sx explicitly to safely merge it
  ...inputProps
}) => {
  const [time, setTime] = useState("");
  
  // 2. Connect to the Agent's state and sensory inputs
  const { reportBehavior } = useKernel();
  
  // Ref to track how long the user is trying to figure out the time
  const focusStartTime = useRef<number>(0);

  const handleTimeChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
    (event) => {
      let { value } = event.target;

      const parts = value.split(":").map(Number);

      let totalSeconds = isNaN(parts[0]) ? 0 : parts[0] * 60 + ~~parts[1];

      if ((parts.length === 2 && isNaN(parts[1])) || isNaN(parts[0])) {
        return;
      }

      if (parts.length === 2 && isNaN(parts[0])) {
        totalSeconds = 0;
      }

      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        totalSeconds = parts[0] * 60 + parts[1];
      }

      // If the user types invalid seconds (e.g., 01:65), auto-correct it
      if (parts.length === 2 && !isNaN(parts[1]) && parts[1] > 60) {
        value = formatTime(totalSeconds);
        
        // TELEMETRY: The UI successfully rescued a user formatting error
        if (reportBehavior) reportBehavior("time_format_autocorrected", 1, "behavior");
      }

      setTime(String(value));
    },
    [reportBehavior]
  );

  // TELEMETRY: Track when the user starts thinking/typing
  const handleFocus = useCallback<NonNullable<TextFieldProps["onFocus"]>>(
    (event) => {
      focusStartTime.current = Date.now();
      if (inputProps.onFocus) inputProps.onFocus(event);
    },
    [inputProps]
  );

  const handleBlur = useCallback<NonNullable<TextFieldProps["onBlur"]>>(
    (event) => {
      // Calculate dwell time
      if (focusStartTime.current > 0) {
        const dwellTime = (Date.now() - focusStartTime.current) / 1000;
        
        // If they stared at the input for more than 8 seconds, they are likely struggling with time conversion math
        if (dwellTime > 8 && reportBehavior) {
          reportBehavior("manual_time_calculation_friction", 2, "behavior");
        }
        focusStartTime.current = 0;
      }

      onBlur?.(event, convertToSeconds(time));
    },
    [onBlur, time, reportBehavior]
  );

  useEffect(() => {
    setTime(String(value));
  }, [value]);

  return (
    <TextField
      label="Time (mm:ss)"
      placeholder="mm:ss"
      value={time}
      onChange={handleTimeChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{ maxLength: 6, pattern: "[0-9][0-9][0-9]:[0-5][0-9]" }}
      size="small"
      sx={{
        width: "120px", // Provides a consistent default size
        // Safely merge incoming adaptive AI styles from parent components
        ...(sx || {})
      }}
      {...inputProps}
    />
  );
};

export default TimeInput;